import { Trophy, Dumbbell, Calendar, Zap, Target, Star, Flame, Award, Moon, Sun, Sunrise, Sunset, Medal, Crown } from 'lucide-react';

const createAchievement = (id, icon, titleKey, descKey, rarity, condition, type) => ({
    id, icon, titleKey, descKey, rarity, condition, type
});

// Helper for nested property access (e.g. 'sbdMax.squat')
const getNestedValue = (obj, path) => path.split('.').reduce((o, i) => o?.[i], obj);

// Helper to generate tiered achievements
const generateTiered = (baseId, icon, metric, thresholds, rarityMap, type) => {
    return thresholds.map((threshold, index) => {
        const rarity = rarityMap[index] || 'common';
        return createAchievement(
            `${baseId}_${threshold}`,
            icon,
            `achievements.${baseId}_title_${threshold}`,
            `achievements.${baseId}_desc_${threshold}`,
            rarity,
            (stats) => getNestedValue(stats, metric) >= threshold,
            type
        );
    });
};

const WORKOUT_MILESTONES = [1, 10, 25, 50, 100, 200, 365, 500, 1000];
const WORKOUT_RARITIES = ['common', 'common', 'common', 'rare', 'epic', 'epic', 'legendary', 'legendary', 'legendary'];

const VOLUME_MILESTONES = [1000, 10000, 50000, 100000, 500000, 1000000, 5000000, 10000000]; // kg
const VOLUME_RARITIES = ['common', 'common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary'];

const MORNING_MILESTONES = [5, 25, 50, 100];
const NIGHT_MILESTONES = [5, 25, 50, 100];
const WEEKEND_MILESTONES = [5, 25, 50, 100];
const SETS_MILESTONES = [100, 500, 1000, 5000, 10000];

const FOLLOWERS_MILESTONES = [1, 10, 50, 100, 500, 1000];
const FOLLOWERS_RARITIES = ['common', 'common', 'rare', 'rare', 'epic', 'legendary'];

const POSTS_MILESTONES = [1, 5, 25, 50, 100];
const POSTS_RARITIES = ['common', 'common', 'rare', 'epic', 'legendary'];

// SBD milestones (kg)
const SQUAT_MILESTONES = [60, 100, 140, 180, 220, 260, 300];
const BENCH_MILESTONES = [40, 60, 80, 100, 120, 140, 160, 180, 200];
const DEADLIFT_MILESTONES = [60, 100, 140, 180, 220, 260, 300, 340];

export const ACHIEVEMENTS = [
    // 1. Workouts Count (The grind)
    ...generateTiered('workouts', 'Dumbbell', 'completedWorkouts', WORKOUT_MILESTONES, WORKOUT_RARITIES, 'consistency'),

    // 2. Total Volume (The strength)
    ...generateTiered('volume', 'Weight', 'totalVolume', VOLUME_MILESTONES, VOLUME_RARITIES, 'strength'),

    // 3. Morning Workouts (Early Birds)
    ...generateTiered('morning', 'Sunrise', 'morningWorkouts', MORNING_MILESTONES, ['common', 'rare', 'epic', 'legendary'], 'consistency'),

    // 4. Night Workouts (Night Owls)
    ...generateTiered('night', 'Moon', 'nightWorkouts', NIGHT_MILESTONES, ['common', 'rare', 'epic', 'legendary'], 'consistency'),

    // 5. Weekend Workouts (Commitment)
    ...generateTiered('weekend', 'Calendar', 'weekendWorkouts', WEEKEND_MILESTONES, ['common', 'rare', 'epic', 'legendary'], 'consistency'),

    // 6. Total Sets (Volume/Dedication)
    ...generateTiered('sets', 'Target', 'totalSets', SETS_MILESTONES, ['common', 'common', 'rare', 'epic', 'legendary'], 'strength'),

    // 7. Community Leader (Followers)
    ...generateTiered('followers', 'Crown', 'followers', FOLLOWERS_MILESTONES, FOLLOWERS_RARITIES, 'community'),

    // 8. Content Creator (Posts)
    ...generateTiered('posts', 'Camera', 'posts', POSTS_MILESTONES, POSTS_RARITIES, 'community'),

    // 9. Squat Master
    ...generateTiered('squat', 'Squat', 'sbdMax.squat', SQUAT_MILESTONES, ['common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary'], 'strength'),

    // 10. Bench Press Master
    ...generateTiered('bench', 'Bench', 'sbdMax.bench', BENCH_MILESTONES, ['common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary', 'legendary', 'legendary'], 'strength'),

    // 11. Deadlift Master
    ...generateTiered('deadlift', 'Deadlift', 'sbdMax.deadlift', DEADLIFT_MILESTONES, ['common', 'common', 'rare', 'rare', 'epic', 'epic', 'legendary', 'legendary'], 'strength'),


    // Special / One-offs
    createAchievement('first_login', 'Star', 'achievements.welcome_title', 'achievements.welcome_desc', 'common', () => true, 'milestone'), // Always unlocked
    createAchievement('tutorial_completed', 'GraduationCap', 'achievements.tutorial_title', 'achievements.tutorial_desc', 'common', (stats) => stats.tutorialCompleted, 'milestone'),

    // Strength Clubs (Global Standards)
    createAchievement('bench_club_100', 'Bench', 'achievements.bench_100_title', 'achievements.bench_100_desc', 'rare', (stats) => getNestedValue(stats, 'sbdMax.bench') >= 100, 'strength_milestone'),
    createAchievement('squat_club_140', 'Squat', 'achievements.squat_140_title', 'achievements.squat_140_desc', 'epic', (stats) => getNestedValue(stats, 'sbdMax.squat') >= 140, 'strength_milestone'),
    createAchievement('deadlift_club_180', 'Deadlift', 'achievements.deadlift_180_title', 'achievements.deadlift_180_desc', 'legendary', (stats) => getNestedValue(stats, 'sbdMax.deadlift') >= 180, 'strength_milestone'),
];

export const getAchievementAssets = (rarity, type, visualHint) => {
    const timestamp = new Date().getTime();

    // Default based on type
    let iconName = type || 'milestone';

    // Override if specific visual hint is provided and valid
    if (visualHint) {
        const hint = visualHint.toLowerCase();
        // Common mappings
        if (hint === 'dumbbell') iconName = 'dumbbell';
        else if (hint === 'weight') iconName = 'strength_v2';
        else if (hint === 'squat') iconName = 'squat';
        else if (hint === 'bench') iconName = 'bench';
        else if (hint === 'deadlift') iconName = 'deadlift';
        else if (hint === 'sunrise') iconName = 'sunrise';
        else if (hint === 'moon') iconName = 'moon';
        else if (hint === 'calendar') iconName = 'calendar';
        else if (hint === 'target') iconName = 'target';
        else if (hint === 'crown') iconName = 'crown';
        else if (hint === 'camera') iconName = 'camera';
        else if (hint === 'star') iconName = 'star';
        // NEW CUSTOM ICONS
        else if (hint === 'graduationcap') iconName = 'tutorial_master';
        else if (id === 'bench_club_100') iconName = 'bench_100';
        else if (id === 'squat_club_140') iconName = 'squat_140';
        else if (id === 'deadlift_club_180') iconName = 'deadlift_180';
    }

    return {
        frame: `/assets/achievements/frame_${rarity || 'common'}.webp?v=${timestamp}`,
        icon: `/assets/achievements/icon_${iconName}.webp?v=${timestamp}`
    };
};

export const getRarityColor = (rarity) => {
    switch (rarity) {
        case 'common': return 'text-neutral-400 bg-neutral-800 border-neutral-700';
        case 'rare': return 'text-blue-400 bg-blue-950/40 border-blue-500/40 shadow-[0_0_15px_-3px_rgba(59,130,246,0.3)]';
        case 'epic': return 'text-purple-400 bg-purple-950/40 border-purple-500/40 shadow-[0_0_15px_-3px_rgba(168,85,247,0.3)]';
        case 'legendary': return 'text-yellow-400 bg-yellow-950/40 border-yellow-500/40 shadow-[0_0_20px_-3px_rgba(234,179,8,0.5)]';
        default: return 'text-neutral-400 bg-neutral-800 border-neutral-700';
    }
};
